import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';
import { DialogOpenEvent } from '@theme/dialog';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    // Capture the drawer before super.connectedCallback() so #updateRefs() doesn't
    // throw MissingRefError after we portal the element out of the component subtree.
    this.menuDrawer = this.querySelector('[ref="menuDrawer"]');

    super.connectedCallback();

    // Portal to document.body so the drawer escapes the header's stacking context
    // and can sit below it via z-index.
    // Propagate --header-group-height to body so the portaled drawer can use it.
    const headerGroupHeight = getComputedStyle(this).getPropertyValue('--header-group-height');
    if (headerGroupHeight) document.body.style.setProperty('--header-group-height', headerGroupHeight.trim());

    document.body.appendChild(this.menuDrawer);

    this.addEventListener('keyup', this.#onKeyUp);
    this.menuDrawer.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    window.matchMedia('(min-width: 1200px)').addEventListener('change', (e) => {
      if (e.matches && this.isOpen) this.close();
    });

    // Close the (mobile) drawer when the search modal opens so the two overlays don't stack.
    // `dialog:open` doesn't bubble, but capture-phase listeners still observe it at ancestors.
    document.addEventListener(DialogOpenEvent.eventName, this.#onSearchOpen, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.menuDrawer?.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener(DialogOpenEvent.eventName, this.#onSearchOpen, { capture: true });
    this.menuDrawer?.remove();
  }

  /**
   * Close the open menu drawer when the search modal opens (the drawer is only ever open at
   * mobile widths). Filtered to the search dialog so other dialogs (e.g. cart) don't close it.
   * @param {Event} event
   */
  #onSearchOpen = (event) => {
    if (!(event.target instanceof Element) || event.target.id !== 'search-modal') return;
    if (this.isOpen) this.close();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /** @param {PointerEvent} event */
  #onPointerDownOutside = (event) => {
    const target = /** @type {Node} */ (event.target);
    if (!this.menuDrawer?.contains(target) && !this.contains(target)) {
      this.close();
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      const isMainDrawer = details === this.refs.details;

      if (isMainDrawer) {
        this.menuDrawer.classList.add('menu-drawer--is-open');
        document.addEventListener('pointerdown', this.#onPointerDownOutside);
        window.addEventListener('resize', this.#onViewportChange);
        this.#positionBelowHeader();
      }

      if (target) {
        this.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = isMainDrawer
        ? this.menuDrawer
        : details.querySelector('.menu-drawer__submenu');
      const focusTarget = isMainDrawer ? this.menuDrawer : details;
      onAnimationEnd(drawer || details, () => trapFocus(focusTarget), { subtree: false });
    });
  }

  /**
   * Pin the drawer flush under the currently-visible header.
   *
   * The drawer's `top`/`height` rely on `--header-group-height`, but the header's
   * ResizeObserver can reset that body variable to the full header-group height as the drawer
   * opens, which no longer matches what is on screen when only part of the group is visible
   * (e.g. the announcement bar scrolled away when the header is not always sticky), leaving a
   * gap between the header and the drawer. Derive the offset from the
   * header's live viewport bottom and set it on the drawer itself so it always sits right
   * below whatever is currently on screen. Re-run on viewport changes (e.g. tablet rotation)
   * via {@link #onViewportChange} while the drawer is open, since the header height can shift.
   */
  #positionBelowHeader() {
    const header = document.querySelector('header-component');
    if (!(header instanceof HTMLElement)) return;

    const top = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    this.menuDrawer.style.setProperty('--header-group-height', `${top}px`);
  }

  /**
   * Re-pin the open drawer under the header when the viewport changes (resize / orientation
   * change). Bound while the main drawer is open and removed on close.
   */
  #onViewportChange = () => {
    this.#positionBelowHeader();
  };

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const isMainDrawer = details === this.refs.details;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    if (isMainDrawer) {
      this.menuDrawer.classList.remove('menu-drawer--is-open');
      document.removeEventListener('pointerdown', this.#onPointerDownOutside);
      window.removeEventListener('resize', this.#onViewportChange);
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree.
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox.
    const drawer = isMainDrawer
      ? this.menuDrawer
      : details.querySelector('.menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (isMainDrawer) {
          removeTrapFocus();
          const openDetails = /** @type {HTMLDetailsElement[]} */ ([
            ...this.querySelectorAll('details[open]:not(accordion-custom > details)'),
            ...this.menuDrawer.querySelectorAll('details[open]:not(accordion-custom > details)'),
          ]);
          openDetails.forEach(reset);
        } else {
          trapFocus(this.menuDrawer);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.menuDrawer.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const searchRoot = details === this.refs.details ? this.menuDrawer : details;
    const content = searchRoot.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
